import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import XHRBackend from 'i18next-xhr-backend';
import eductsConfig from '@exporo/educts-config';
import Vue from 'vue';
import '@exporo/educts-component-types';
import { Prop, Component } from 'vue-property-decorator';

var globals = (Vue, options) => {
    // $i18n global
    const i18nName = `$${options.prefix}${options.names.i18n}`;
    if (!Object.prototype.hasOwnProperty.call(Vue, i18nName)) {
        Object.defineProperty(Vue.prototype, i18nName, {
            get() {
                return i18next;
            },
        });
    }
    // $t global
    const tName = `$${options.prefix}${options.names.t}`;
    if (!Object.prototype.hasOwnProperty.call(Vue, tName)) {
        Object.defineProperty(Vue.prototype, tName, {
            get() {
                // add non-breakable space as loading placeholder to avoid to much bouncing
                return i18next.isInitialized ? i18next.t.bind(i18next) : () => '\u00A0';
            },
        });
    }
};

const normalizeValue = (value, language) => {
    const normalizedValue = {
        path: '',
        arguments: {},
    };
    if (language) {
        normalizedValue.arguments = { ...normalizedValue.arguments, language };
    }
    if (typeof value === 'object') {
        normalizedValue.path = value.path || 'undefined_key_path';
        normalizedValue.arguments = {
            ...normalizedValue.arguments,
            ...(value.arguments || {}),
        };
    }
    else {
        normalizedValue.path = value;
    }
    return normalizedValue;
};
const applyTranslation = (element, binding, vnode) => {
    if (!vnode.context.$i18n.isInitialized) {
        element.hidden = true;
        element.i18nKey = binding.value;
    }
    else {
        const normalizedValue = normalizeValue(binding.value, binding.arg);
        element.hidden = false;
        element.textContent = vnode.context.$i18n.t(normalizedValue.path, {
            ...normalizedValue.arguments,
        });
    }
};
const languageChanged = (element, vnode) => {
    if (!vnode.context || !vnode.context.$i18n) {
        return false;
    }
    return element.i18nLanguage !== vnode.context.$i18n.language;
};
const bindingChanged = (binding) => {
    return binding.value !== binding.oldArg || binding.arg !== binding.oldArg;
};
const onInitialized = (vnode) => () => {
    vnode.context.$forceUpdate();
};
const onLanguageChanged = (vnode) => () => {
    vnode.context.$forceUpdate();
};
const isSecureVNode = (vnode) => {
    return !!vnode.context;
};
const directive = {
    bind: (element, binding, vnode) => {
        if (!isSecureVNode(vnode)) {
            // eslint-disable-next-line no-console
            console.error('Could not bind v-t directive to a vnode without a context.');
            return;
        }
        if (!vnode.context.$i18n) {
            // eslint-disable-next-line no-console
            console.warn('Unable to find i18n context variable! Falling back to i18next');
            vnode.context.$i18n = i18next;
        }
        vnode.context.$i18n.on('languageChanged', onInitialized(vnode));
        vnode.context.$i18n.on('initialized', onLanguageChanged(vnode));
        applyTranslation(element, binding, vnode);
    },
    update: (element, binding, vnode) => {
        if (isSecureVNode(vnode) && (languageChanged(element, vnode) || bindingChanged(binding))) {
            applyTranslation(element, binding, vnode);
        }
    },
    unbind: (element, binding, vnode) => {
        element.i18nKey = undefined;
        element.hidden = false;
        if (!isSecureVNode(vnode)) {
            return;
        }
        vnode.context.$i18n.off('languageChanged', onInitialized(vnode));
        vnode.context.$i18n.off('initialized', onLanguageChanged(vnode));
    },
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

let default_1 = class default_1 extends Vue {
    constructor() {
        super(...arguments);
        this.isInitialized = false;
    }
    get message() {
        return i18next.t(this.path, this.default, this.arguments);
    }
    created() {
        if (!i18next.isInitialized) {
            this.isInitialized = false;
            i18next.on('initialized', () => {
                this.$emit('initialized');
                this.isInitialized = true;
            });
        }
        else {
            this.isInitialized = true;
        }
        i18next.on('languageChanged', () => {
            this.$forceUpdate();
        });
    }
    get rootClasses() {
        const classes = {};
        if (this.classes.root) {
            classes[this.classes.root] = true;
        }
        return classes;
    }
};
__decorate([
    Prop({ required: false, default: false }),
    __metadata("design:type", String)
], default_1.prototype, "path", void 0);
__decorate([
    Prop({ required: false, default: () => ({}) }),
    __metadata("design:type", Object)
], default_1.prototype, "arguments", void 0);
__decorate([
    Prop({ required: false }),
    __metadata("design:type", Object)
], default_1.prototype, "default", void 0);
__decorate([
    Prop({ required: false, default: () => ({}) }),
    __metadata("design:type", Object)
], default_1.prototype, "classes", void 0);
__decorate([
    Prop({ required: false, default: 't-string' }),
    __metadata("design:type", String)
], default_1.prototype, "testIdentifier", void 0);
default_1 = __decorate([
    Component
], default_1);
var script = default_1;

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.isInitialized
    ? _c(
        "span",
        { class: _vm.rootClasses, attrs: { "data-test": _vm.testIdentifier } },
        [_vm._v(_vm._s(_vm.message))]
      )
    : _vm._e()
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

const LOCALE_WHITELIST = ['de-DE', 'nl-NL', 'fr-FR', 'en-GB'];
class I18nPlugin {
    install(Vue, options) {
        if (I18nPlugin.installed) {
            return;
        }
        I18nPlugin.installed = true;
        this.i18nextConfig = {
            ...I18nPlugin.getDefaultOptions(),
            ...I18nPlugin.getEductsOptions(),
            ...(options && options.i18next ? options.i18next : {}),
        };
        i18next
            .use(LanguageDetector)
            .use(XHRBackend)
            // eslint-disable-next-line no-console
            .init(I18nPlugin.getI18NextConfig(this.i18nextConfig), err => err && console.error(err));
        i18next.on('missingKey', this.i18nextConfig.onMissingKey);
        I18nPlugin.installGlobals(Vue, options);
        I18nPlugin.installDirectives(Vue, options);
        I18nPlugin.installComponents(Vue, options);
    }
    static installGlobals(Vue, options) {
        const globalsOptions = {
            prefix: '',
            names: {
                i18n: 'i18n',
                t: 't',
            },
            ...(options && options.globals ? options.globals : {}),
            enable: true,
        };
        if (globalsOptions.enable) {
            globals(Vue, globalsOptions);
        }
    }
    static installDirectives(Vue, options) {
        const directiveOptions = {
            enable: true,
            prefix: '',
            names: {
                t: 't',
            },
            ...(options && options.directives ? options.directives : {}),
        };
        if (directiveOptions.enable) {
            // v-t directive
            let tName = directiveOptions.names.t;
            if (directiveOptions.prefix)
                tName = `${directiveOptions.prefix}-${tName}`;
            Vue.directive(tName, directive);
        }
    }
    static installComponents(Vue, options) {
        const componentsOptions = {
            global: true,
            prefix: '',
            names: {
                t: 't',
            },
            ...(options && options.components ? options.components : {}),
        };
        if (componentsOptions.global) {
            Vue.component(`${componentsOptions.prefix}${componentsOptions.names.t}`, __vue_component__);
        }
    }
    static getI18NextConfig(options) {
        return {
            debug: options.debug,
            // Namespace behavior to support multiple project sources
            ns: options.namespaces,
            defaultNS: options.defaultNamespace,
            fallbackNS: options.fallbackNamespace,
            // Fallback Locale
            fallbackLng: options.fallbackLng,
            // Lets keep the de-DE schema
            cleanCode: true,
            // locales loading schema
            load: options.load,
            // To enable SSR
            preload: [options.fallbackLng],
            // Avoid async loading behavior
            initImmediate: true,
            // Language detection
            detection: {
                order: ['querystring', 'navigator', 'htmlTag'],
                lookupQuerystring: 'lang',
            },
            // XHR Backend
            backend: {
                loadPath: `${options.publicPath}/{{lng}}/{{ns}}.json`,
            },
            // Hardcoded Whitelist to avoid no well formatted locales
            whitelist: LOCALE_WHITELIST,
        };
    }
    static getEductsOptions() {
        const eductsOptions = {};
        if (eductsConfig.has('i18n.fallbackLocale')) {
            eductsOptions.fallbackLng = eductsConfig.get('i18n.fallbackLocale');
        }
        if (eductsConfig.has('i18n.projects')) {
            eductsOptions.namespaces = eductsConfig.get('i18n.projects');
        }
        if (eductsConfig.has('i18n.projects')) {
            const [defaultNamespace] = eductsConfig.get('i18n.projects');
            eductsOptions.defaultNamespace = defaultNamespace;
            eductsOptions.fallbackNamespace = defaultNamespace;
        }
        if (eductsConfig.has('i18n.publicPath')) {
            eductsOptions.publicPath = eductsConfig.get('i18n.publicPath');
        }
        return eductsOptions;
    }
    static getDefaultOptions() {
        return {
            resources: {},
            debug: false,
            fallbackLng: 'en-EN',
            namespaces: ['messages'],
            defaultNamespace: 'messages',
            fallbackNamespace: 'messages',
            publicPath: '/locales',
            load: 'currentOnly',
            detection: {
                order: ['querystring', 'navigator', 'htmlTag'],
            },
            // Events
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onMissingKey: () => { },
        };
    }
}
I18nPlugin.installed = false;
var Plugin = new I18nPlugin();

export default Plugin;
export { __vue_component__ as t };
//# sourceMappingURL=main.esnext.js.map
